/* eslint-disable react/no-multi-comp */
// externals
import type { FC, PropsWithChildren, ReactNode } from 'react';
import { Fragment, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// libraries
import { Typography } from '@interstate/components/Typography';
import { Box } from '@interstate/components/Box';
import { Tooltip } from '@interstate/components/Tooltip';
import { ListBulletIcon } from '@interstate/components/Icons/ListBulletIcon';
import { BuildingOfficeIcon } from '@interstate/components/Icons/BuildingOfficeIcon';
import { UserIcon } from '@interstate/components/Icons/UserIcon';
import { UserPlusIcon } from '@interstate/components/Icons/UserPlusIcon';
import { Action } from '@interstate/components/Action';
import type { CommonConsumer } from '@makemydeal/dr-platform-types';
import { AiLogo } from '@makemydeal/dr-shared-components';

import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import * as quoteSelectors from '@makemydeal/dr-dash-store/selectors/quoteSelectors';
import * as editCustomerActionCreators from '@makemydeal/dr-dash-store/actions/editCustomerActionCreators';
import * as dealXgDetailsSelectors from '@makemydeal/dr-dash-store/selectors/dealXgDetailsSelectors';
import * as configSelectors from '@makemydeal/dr-dash-store/selectors/config';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';
import { addCustomer, isBuyerMissing } from '@makemydeal/dr-activities-customer-card';
import { dealLockHooks } from '../hooks';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { formatPostalCodeForDisplay, offerSessionUtils } from '@makemydeal/dr-shared-ui-utils';
import { BUYER } from '@makemydeal/dr-shared-types';

// components
import { useCustomerUI } from '../customerUI/useCustomerUI';
import { CustomerSideBarTabEnum } from '../customerUI/CustomerSidebarProps';
import { CoBuyerLink } from './CoBuyerLink';
import { VehicleInsuranceLink } from './VehicleInsurance';
import { VehicleComplianceLink } from './VehicleCompliance';
import EditCustomerSlideout from './EditCustomerSlideout';
import CustomerInboxButton from './CustomerInboxButton';
import { DeskingModeCTA } from './DeskingModeCTA';
import { RecentDealsButton } from './RecentDealsButton';

// styles
import {
    HeaderBuyerContainer,
    HeaderActionsContainer,
    HeaderContentRow,
    HeaderActionsSeparator,
    HeaderEnhancedShopperDetails,
    HeaderShopperNameAndActions,
    ShopperDetailItem,
    HeaderDealContainer,
    HeaderDetailsAndAssistantRow,
    HeaderAssistantSlot,
    HeaderModeSwitchArea
} from './Header.style.interstate';

// utils
import { formatPhoneNumber, toProperCase } from '../utils/formatUtils';
import { FieldWithFallback } from '../utils/fieldWithFallback';
import { NOT_AVAILABLE_DASH } from '../constants';
import ActionWithErrorBadge from './ActionWithErrorBadge';
import { HeaderActionItem, MODE_SWITCH_WIDTH_VAR } from './MvHeaderLayout.style';

type Shopper = {
    firstName?: string;
    lastName?: string;
    middleName?: string;
    suffix?: string;
    streetAddress?: string;
    city?: string;
    state?: string;
    zip?: string;
    county?: string;
    phone?: string;
};

type ShopperInfoProps = {
    shopper: Shopper;
    hasCustomer?: boolean;
};

const ShopperName: FC<ShopperInfoProps> = ({ shopper, hasCustomer = true }) => {
    if (!hasCustomer) {
        return (
            <ActionWithErrorBadge entityName={BUYER} xOffset="-1px" yOffset="4px">
                <span>Add Customer</span>
            </ActionWithErrorBadge>
        );
    }

    const hasName = !!(shopper.firstName || shopper.lastName);
    if (!hasName) {
        return (
            <ActionWithErrorBadge entityName={BUYER} xOffset="-1px" yOffset="4px">
                <span>Customer</span>
            </ActionWithErrorBadge>
        );
    }

    let prettySuffix = '';
    if (shopper.suffix) {
        prettySuffix = toProperCase(shopper.suffix.replace(/[^a-zA-Z]/g, ''));
    }
    return (
        <ActionWithErrorBadge entityName={BUYER} xOffset="-1px" yOffset="4px">
            <span>
                <FieldWithFallback value={shopper.firstName} fallback="" suffix={' '} />
                <FieldWithFallback value={shopper.middleName} fallback="" suffix={' '} />
                <FieldWithFallback value={shopper.lastName} fallback="" suffix={' '} />
                <FieldWithFallback value={prettySuffix} fallback="" suffix={'.'} />
            </span>
        </ActionWithErrorBadge>
    );
};

// Wraps rather than replaces the entry point, so the tooltip goes away on its own once it is enabled.
const AddCustomerTooltip: FC<PropsWithChildren<{ show: boolean }>> = ({ show, children }) =>
    show ? (
        <Tooltip toolTipContent="Save a quote to enable adding a buyer." position="bottom" size="large">
            <span data-testid="add-customer-disabled-tooltip">{children}</span>
        </Tooltip>
    ) : (
        <>{children}</>
    );

const ShopperAddress: FC<ShopperInfoProps> = ({ shopper }) => {
    const isPostalCodeDigitsOnlyValidationEnabled = useSelector(featureToggleSelectors.isPostalCodeDigitsOnlyValidationEnabled);
    const zip = isPostalCodeDigitsOnlyValidationEnabled ? formatPostalCodeForDisplay(shopper.zip) : shopper.zip;

    return (
        <>
            <FieldWithFallback value={shopper.streetAddress} suffix={', '} />
            <FieldWithFallback value={shopper.city} suffix={', '} />
            <FieldWithFallback value={shopper.state} suffix={', '} />
            <FieldWithFallback value={zip} suffix={', '} />
            <FieldWithFallback value={shopper.county} />
        </>
    );
};

const ShopperPhone: FC<ShopperInfoProps> = ({ shopper }) => {
    const formattedPhone = formatPhoneNumber(shopper.phone!);
    return formattedPhone ? <FieldWithFallback value={formattedPhone} /> : NOT_AVAILABLE_DASH;
};

export const HeaderRowInterstate: FC<{ assistantEntry?: ReactNode }> = ({ assistantEntry }) => {
    const dispatch = useDispatch();
    const { showCustomerCard } = useCustomerUI();
    const shopper = useSelector(offerReduxSelectors.getShopperInfo);
    const commonConsumerId = useSelector(offerReduxSelectors.getShopperCommonConsumerId);
    const isQuoteMode = useSelector(quoteSelectors.getQuoteMode);
    const quoteName = useSelector(quoteSelectors.getQuoteName);
    const hasCustomer = !isQuoteMode;
    const isEnableCustomerInQuoteModeEnabled = useSelector(featureToggleSelectors.isEnableCustomerInQuoteModeEnabled);
    // A customer needs a saved quote to attach to, so the entry point waits for the first DealXg record.
    const isQuoteNotYetSaved = useSelector(quoteSelectors.getIsQuoteModeWithoutDealXgRecord);
    const canAddCustomerInQuoteMode = isQuoteMode && isEnableCustomerInQuoteModeEnabled && !isQuoteNotYetSaved;
    const showSaveQuoteFirstTooltip = isEnableCustomerInQuoteModeEnabled && isQuoteNotYetSaved;
    const isNativeAddCustomerEnabled = useSelector(featureToggleSelectors.isNativeAddCustomerEnabled);
    // Not `!hasCustomer`: a deal with a buyer but no vehicle is still quote mode, and must keep editing.
    const hasNoBuyer = isBuyerMissing(commonConsumerId, shopper.lastName);
    const isBusinessShopper = shopper.customerType === 'Business';
    const isBusinessShopperFromStore = useSelector(offerSelectors.getIsBusinessShopper);
    const isEcommerceDeal = useSelector(dealXgDetailsSelectors.getIsEcommerceDeal);
    const isCreditProviderRouteOne = useSelector(dealerSelectors.isCreditProviderRouteOne);
    const isDealListLink = useSelector(configSelectors.getDealListUrl);

    const customerEditedCallback = (customer: CommonConsumer) => {
        dispatch(offerReduxActionCreators.updatedShopperLocationFromConsumer(shopper, customer));
    };

    // Get locked state to disable buttons in customer sidebar when deal is finalized
    const isLocked = dealLockHooks.useIsDealLocked();

    const prNumber = offerSessionUtils.getPrNumber();

    const isComplianceLinkEnabled = useMemo(() => {
        return !isCreditProviderRouteOne || isEcommerceDeal;
    }, [isEcommerceDeal]);

    const isMVRecentDealsEnabled = useSelector(featureToggleSelectors.isMVRecentDealsEnabled);
    const isMvRedesignEnabled = useSelector(featureToggleSelectors.isMvRedesignEnabled);
    const ActionItem = isMvRedesignEnabled ? HeaderActionItem : Fragment;

    const hasAddressData = shopper.streetAddress || shopper.city || shopper.state || shopper.zip || shopper.county;
    const hasPhoneData = shopper.phone;
    const hasEmailData = shopper.email;

    const shopperFields = [
        ...(hasAddressData
            ? [
                  {
                      key: 'address',
                      gridSpan: 2,
                      label: 'Address',
                      dataTestId: 'shopper-address',
                      component: <ShopperAddress shopper={shopper} />
                  }
              ]
            : []),
        ...(hasPhoneData
            ? [
                  {
                      key: 'phone',
                      label: 'Phone',
                      dataTestId: 'shopper-phone-number',
                      component: <ShopperPhone shopper={shopper} />
                  }
              ]
            : []),
        ...(hasEmailData
            ? [
                  {
                      key: 'email',
                      label: 'Email Address',
                      dataTestId: 'shopper-email',
                      component: shopper.email
                  }
              ]
            : []),
        ...(isBusinessShopper
            ? [
                  {
                      key: 'businessRep',
                      label: 'Signer',
                      dataTestId: 'shopper-business-rep',
                      component: <ShopperName shopper={shopper} />
                  }
              ]
            : [])
    ];

    const shopperDetails = (
        <HeaderEnhancedShopperDetails>
            {shopperFields.map(({ key, gridSpan, component, dataTestId }, index) => {
                const hasSeparator = true;
                const isLastItem = index === shopperFields.length - 1;
                const isPiiField =
                    dataTestId === 'shopper-address' || dataTestId === 'shopper-phone-number' || dataTestId === 'shopper-email';

                return (
                    <ShopperDetailItem
                        gridSpan={gridSpan}
                        fieldKey={key}
                        key={key}
                        isLastItem={isLastItem}
                        hasSeparator={hasSeparator}
                    >
                        <div data-testId={dataTestId} className={isPiiField ? 'pii' : ''}>
                            {component}
                        </div>
                    </ShopperDetailItem>
                );
            })}
        </HeaderEnhancedShopperDetails>
    );

    return (
        <HeaderContentRow data-testid="header-row-content">
            <HeaderBuyerContainer>
                <HeaderDealContainer>
                    <HeaderShopperNameAndActions className="compact-ui-header-shopper-name-and-actions">
                        <Box display="flex" alignItems="center" gap="1rem">
                            {isMVRecentDealsEnabled && <RecentDealsButton />}
                            {quoteName && (
                                <Typography variant="h3" tag="div" color="base.color.blue.700" data-testid="quote-name">
                                    {quoteName}
                                </Typography>
                            )}
                            <Typography
                                variant="h3"
                                tag={commonConsumerId && hasCustomer ? 'a' : 'div'}
                                color="base.color.blue.700"
                                data-testid="shopper-name-link"
                                sx={{ cursor: 'pointer', display: 'flex', flexDirection: 'row', gap: '1rem' }}
                            >
                                <AddCustomerTooltip show={showSaveQuoteFirstTooltip}>
                                    <div
                                        className="shopper-name"
                                        data-testid="shopper-name"
                                        aria-disabled={isQuoteMode && !canAddCustomerInQuoteMode}
                                        style={{
                                            cursor: 'pointer',
                                            opacity: isQuoteMode && !canAddCustomerInQuoteMode ? 0.5 : 1
                                        }}
                                        onClick={() => {
                                            if (isQuoteMode && !canAddCustomerInQuoteMode) return;
                                            if (hasNoBuyer && isNativeAddCustomerEnabled) {
                                                dispatch(addCustomer());
                                            } else {
                                                dispatch(editCustomerActionCreators.showEditBuyerSlideout());
                                            }
                                            dispatch(interactionAnalyticsActionCreators.customerNameOrCoBuyerLink());
                                        }}
                                    >
                                        <Box component="span" sx={{ position: 'relative', top: '0.1rem', marginRight: '0.3rem' }}>
                                            {!hasCustomer ? (
                                                <UserPlusIcon data-testid="add-customer-icon" />
                                            ) : isBusinessShopper ? (
                                                <BuildingOfficeIcon data-testid="business-customer-icon" />
                                            ) : (
                                                <UserIcon data-testid="individual-customer-icon" />
                                            )}
                                        </Box>
                                        {hasCustomer && isBusinessShopper ? (
                                            <FieldWithFallback value={shopper.business?.legalName} fallback="" />
                                        ) : (
                                            <ShopperName shopper={shopper} hasCustomer={hasCustomer} />
                                        )}
                                    </div>
                                </AddCustomerTooltip>
                            </Typography>
                            <Tooltip toolTipContent="Deal Pulse" position="bottom" size="sm">
                                <Box
                                    data-testid="ai-logo-wrapper"
                                    sx={{ cursor: 'pointer', opacity: commonConsumerId ? 1 : 0.5 }}
                                    onClick={() => {
                                        commonConsumerId &&
                                            showCustomerCard(true, {
                                                customerEditedCallback,
                                                readOnlyFields: isLocked,
                                                tab: CustomerSideBarTabEnum.Deal
                                            });
                                    }}
                                >
                                    <AiLogo />
                                </Box>
                            </Tooltip>
                        </Box>
                        <HeaderActionsContainer className="compact-ui-header-actions-container">
                            {!isBusinessShopperFromStore && (
                                <ActionItem>
                                    <HeaderActionsSeparator className="compact-ui-header-actions-separator" />
                                    <CustomerInboxButton
                                        dataTestId="buyer-inbox-btn"
                                        disabled={!commonConsumerId}
                                        onClick={() => {
                                            commonConsumerId &&
                                                showCustomerCard(true, {
                                                    customerEditedCallback,
                                                    readOnlyFields: isLocked,
                                                    tab: 'inbox'
                                                });
                                        }}
                                    />
                                </ActionItem>
                            )}

                            <ActionItem>
                                <HeaderActionsSeparator className="compact-ui-header-actions-separator" />
                                <VehicleInsuranceLink disabled={!hasCustomer} />
                            </ActionItem>

                            {isComplianceLinkEnabled && (
                                <ActionItem>
                                    <HeaderActionsSeparator className="compact-ui-header-actions-separator" />
                                    <VehicleComplianceLink disabled={!hasCustomer} />
                                </ActionItem>
                            )}
                            <ActionItem>
                                <HeaderActionsSeparator $isThick className="compact-ui-header-actions-separator" />
                                <CoBuyerLink
                                    isAddCoBuyerDisabled={!hasCustomer}
                                    addCoBuyerDisabledTooltip={
                                        isEnableCustomerInQuoteModeEnabled ? 'Add the primary buyer first.' : undefined
                                    }
                                />
                            </ActionItem>
                        </HeaderActionsContainer>
                    </HeaderShopperNameAndActions>
                    <HeaderModeSwitchArea>
                        <DeskingModeCTA toggleWidth={isMvRedesignEnabled ? `var(${MODE_SWITCH_WIDTH_VAR})` : undefined} />
                        {!isMVRecentDealsEnabled && (
                            <Action
                                size="md"
                                data-testid="deal-list-btn"
                                onClick={() => {
                                    dispatch(interactionAnalyticsActionCreators.clickDealListLink());
                                    window.location.assign(isDealListLink);
                                }}
                                sx={{ cursor: 'pointer', display: 'flex', whiteSpace: 'nowrap' }}
                                actionIcon={{
                                    icon: <ListBulletIcon />,
                                    start: true
                                }}
                            >
                                Deal List
                            </Action>
                        )}
                    </HeaderModeSwitchArea>
                </HeaderDealContainer>

                {assistantEntry ? (
                    <HeaderDetailsAndAssistantRow>
                        {shopperDetails}
                        <HeaderAssistantSlot data-testid="header-assistant-slot">{assistantEntry}</HeaderAssistantSlot>
                    </HeaderDetailsAndAssistantRow>
                ) : (
                    shopperDetails
                )}
            </HeaderBuyerContainer>
            {prNumber && (
                <Typography data-testid="header-row-pr-number" variant="body-md" tag="div" color="base.color.blue.700">
                    PR - {prNumber}
                </Typography>
            )}
            <EditCustomerSlideout />
        </HeaderContentRow>
    );
};
